import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Package,
  Users,
  DollarSign,
  LogOut,
  Watch,
  Store,
  Wrench,
  User,
  Shield,
  Network,
  MessageCircle,
  Heart,
  Calendar,
  Calculator,
  FileText,
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const baseNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/inventory', label: 'Inventory', icon: Package },
    { path: '/clients', label: 'Clients', icon: Users },
    { path: '/wishlists', label: 'Wish Lists', icon: Heart },
    { path: '/appointments', label: 'Calendar', icon: Calendar },
    { path: '/sales', label: 'Sales', icon: DollarSign },
    { path: '/invoices', label: 'Invoices', icon: FileText },
    { path: '/accounting', label: 'Accounting', icon: Calculator },
    { path: '/servicing', label: 'Servicing', icon: Wrench },
    { path: '/marketplace', label: 'Marketplace', icon: Store },
    { path: '/network', label: 'Network', icon: Network },
    { path: '/messages', label: 'Messages', icon: MessageCircle },
    { path: '/profile', label: 'Profile', icon: User },
  ];

  const navItems = user?.is_admin
    ? [...baseNavItems, { path: '/admin', label: 'Admin', icon: Shield, isAdmin: true }]
    : baseNavItems;

  const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/'));

  return (
    <div className="flex h-screen bg-[#fafafa]">
      <aside className="w-56 flex-shrink-0 bg-[#0a0a0a] text-white flex flex-col print:hidden">
        <div className="p-4 border-b border-white/[0.06]">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
              <Watch className="w-4 h-4 text-[#0a0a0a]" />
            </div>
            <span className="font-semibold text-[15px] tracking-tight">Dial-Grid</span>
          </Link>
          <div className="mt-4 px-3 py-2.5 rounded-lg bg-white/[0.04]">
            <p className="text-[10px] text-white/40 uppercase tracking-wider">Signed in</p>
            <p className="text-sm font-medium text-white/90 truncate">{user?.dealer_name}</p>
            <p className="text-xs text-white/40 truncate">{user?.email}</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                item.isAdmin
                  ? isActive(item.path)
                    ? 'bg-red-500/15 text-red-300'
                    : 'text-white/55 hover:bg-white/[0.04] hover:text-white/85'
                  : isActive(item.path)
                  ? 'bg-white/[0.08] text-white'
                  : 'text-white/55 hover:bg-white/[0.04] hover:text-white/85'
              }`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0 opacity-80" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="p-2 border-t border-white/[0.06]">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-white/45 hover:bg-white/[0.04] hover:text-white/70 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex-shrink-0 h-14 bg-white border-b border-[#e5e5e5] px-5 md:px-6 flex items-center justify-between print:hidden">
          <h1 className="text-[15px] font-semibold text-[#0a0a0a] tracking-tight">
            {navItems.find((item) => isActive(item.path))?.label || 'Dashboard'}
          </h1>
          <Link
            to="/profile"
            className="flex items-center gap-3 rounded-lg py-1.5 pr-1 pl-2.5 hover:bg-[#f5f5f5] transition-colors"
          >
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-[#0a0a0a]">{user?.dealer_name}</p>
              <p className="text-xs text-[#737373]">{user?.email}</p>
            </div>
            <div className="w-8 h-8 rounded-full bg-[#0a0a0a] flex items-center justify-center text-white font-medium text-sm">
              {user?.dealer_name?.charAt(0) || '?'}
            </div>
          </Link>
        </header>
        <main className="flex-1 overflow-y-auto p-5 md:p-6 print:p-0 bg-[#fafafa]">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
